import {
	type ChangeEvent,
	type RefObject,
	useEffect,
	useRef,
	useState,
} from 'react';
import { FaXmark } from 'react-icons/fa6';
import { IoFlash } from 'react-icons/io5';
import { MdFlipCameraIos } from 'react-icons/md';

import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
} from '@hubmaster/ui/select';
import { Toggle } from '@hubmaster/ui/toggle';
import { Portal } from '@radix-ui/react-portal';
import {
	type CameraDevice,
	Html5Qrcode,
	Html5QrcodeSupportedFormats,
} from 'html5-qrcode';

import beepSoundNotFound from './bip-notfound.mp3';
import beepSound from './bip-scanner.mp3';

export const beepSounds = {
	read: beepSound,
	notFound: beepSoundNotFound,
};

interface BarcodeScannerProps {
	onRead: (value: string) => void;
	setActiveScanner: (value: any) => void;
	activeScanner: boolean;
	type: 'barcode' | 'qrcode';
}

let beep: HTMLAudioElement | null = null;

if (typeof window !== 'undefined') {
	beep = new Audio(beepSound);
}

function soundScannerSuccess() {
	if (beep) {
		beep.play();
	}
	if (typeof navigator !== 'undefined' && navigator.vibrate) {
		navigator.vibrate([100]);
	}
}

function getBestCameraForBarcode(
	cameras: CameraDevice[],
): CameraDevice | undefined {
	const score = (label: string) => {
		const l = label.toLowerCase();
		// Evitar: frontal e ultrawide (focal curta — ruim para barcodes)
		if (
			l.includes('front') ||
			l.includes('selfie') ||
			l.includes('user') ||
			l.includes('frontal') ||
			l.includes('wide') ||
			l.includes('ultra')
		)
			return -1;
		// Melhor: telefoto (focal longa — ótimo para barcodes)
		if (l.includes('tele') || l.includes('zoom')) return 3;
		// Bom: câmera traseira principal
		if (
			l.includes('back') ||
			l.includes('rear') ||
			l.includes('traseiro') ||
			l.includes('environment')
		)
			return 2;
		// Desconhecida: provavelmente traseira principal em labels vazios (iOS)
		return 1;
	};
	return [...cameras].sort((a, b) => score(b.label) - score(a.label))[0];
}

export function BarcodeScanner({
	onRead,
	activeScanner,
	setActiveScanner,
	type: _type,
}: BarcodeScannerProps) {
	const [devices, setDevices] = useState<CameraDevice[]>([]);
	const [cameraActiveId, setCameraActiveId] = useState<string | undefined>();
	const [isScanning, setIsScanning] = useState<boolean>(false);
	const scanRef = useRef<Html5Qrcode | null>(null);

	// Carrega câmeras disponíveis para o seletor, sem tocar no html5-qrcode ainda
	useEffect(() => {
		Html5Qrcode.getCameras().then((cameras) => {
			setDevices(cameras);
			const camerasId = cameras.map((camera) => camera.id);
			const cameraDefault = localStorage.getItem('cameraActiveId');
			if (cameraDefault && camerasId.includes(cameraDefault)) {
				setCameraActiveId(cameraDefault);
			} else {
				const best = getBestCameraForBarcode(cameras);
				if (best) setCameraActiveId(best.id);
			}
		});
	}, []);

	useEffect(() => {
		if (true === activeScanner) {
			startScan();
		} else if (scanRef.current?.isScanning) {
			scanRef.current.stop().then(() => {
				scanRef.current?.clear();
				setIsScanning(false);
			});
		}
	}, [cameraActiveId, activeScanner]);

	// Inicializa o html5-qrcode na primeira abertura — o Portal já garantiu
	// que #scanner_barcode existe no DOM antes desta função ser chamada.
	async function initializeScanner(): Promise<boolean> {
		if (scanRef.current) return true;
		const el = document.getElementById('scanner_barcode');
		if (!el) return false;
		scanRef.current = new Html5Qrcode('scanner_barcode', {
			// Usa a BarcodeDetector API nativa do browser quando disponível (muito mais rápida)
			useBarCodeDetectorIfSupported: true,
			verbose: false,
			// Todos os formatos comuns — QR code + barras — numa única passagem
			formatsToSupport: [
				Html5QrcodeSupportedFormats.EAN_13,
				Html5QrcodeSupportedFormats.EAN_8,
				Html5QrcodeSupportedFormats.CODE_128,
				Html5QrcodeSupportedFormats.CODE_39,
				Html5QrcodeSupportedFormats.UPC_A,
				Html5QrcodeSupportedFormats.UPC_E,
				Html5QrcodeSupportedFormats.ITF,
				Html5QrcodeSupportedFormats.CODABAR,
				Html5QrcodeSupportedFormats.QR_CODE,
				Html5QrcodeSupportedFormats.DATA_MATRIX,
				Html5QrcodeSupportedFormats.PDF_417,
				Html5QrcodeSupportedFormats.AZTEC,
			],
		});
		return true;
	}

	async function startScan() {
		const ready = await initializeScanner();
		if (!ready) {
			setActiveScanner('popup');
			return;
		}

		if (scanRef.current!.isScanning) {
			await scanRef.current!.stop();
			scanRef.current!.clear();
			setIsScanning(false);
		}

		const configScan: MediaTrackConstraints = cameraActiveId
			? { deviceId: cameraActiveId }
			: { facingMode: 'environment' };

		scanRef
			.current!.start(
				configScan,
				{
					// 25 fps — mais tentativas por segundo sem custo alto (BarcodeDetector é nativo)
					fps: 25,
					aspectRatio: window.innerHeight / window.innerWidth,
					// Cobre ~75% da largura e ~50% da altura: lê barcodes horizontais e QR codes
					qrbox: (w, h) => ({
						width: Math.round(Math.min(w * 0.75, 320)),
						height: Math.round(Math.min(h * 0.5, 280)),
					}),
				},
				(data) => {
					onRead(data);
					soundScannerSuccess();
					scanRef.current?.stop().then(() => {
						setIsScanning(false);
						setActiveScanner(false);
					});
				},
				() => setIsScanning(!!scanRef.current?.isScanning),
			)
			.then(() => setIsScanning(!!scanRef.current?.isScanning))
			.catch((e) => {
				setActiveScanner('popup');
				console.error(e);
			});
	}

	function updateCameraActiveId(id: string) {
		setCameraActiveId(id);
		localStorage.setItem('cameraActiveId', id);
		localStorage.removeItem('activeFlash');
		localStorage.removeItem('zoom');
	}

	// Portal renderiza no document.body — fora de qualquer overflow:hidden do app.
	// Isso resolve o iOS Safari onde fixed dentro de overflow:hidden não intercepta toques.
	// Toolbar abaixo do vídeo para não sobrepor o <video> (camada nativa no iOS).
	return (
		<Portal>
			<div
				className={`fixed inset-0 z-[9998] flex flex-col bg-black ${activeScanner ? '' : 'hidden'}`}
			>
				{/* html5-qrcode popula este div com o <video> */}
				<div id="scanner_barcode" />

				{/* Controles abaixo do vídeo — nunca sobrepostos */}
				<div className="absolute inset-x-0 top-4 flex shrink-0 items-center justify-between px-4 py-3">
					<div className="flex gap-2">
						{isScanning && scanRef.current?.isScanning && (
							<Select
								onValueChange={(value: any) => updateCameraActiveId(value)}
								defaultValue={cameraActiveId}
							>
								<SelectTrigger
									className="h-10 w-10 justify-center rounded-full bg-white/10 px-1 py-1"
									showIcon={false}
								>
									<MdFlipCameraIos size={20} className="text-white" />
								</SelectTrigger>
								<SelectContent className="bg-background z-[9999]">
									{devices.map((device) => (
										<SelectItem key={device.id} value={device.id}>
											{device.label}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						)}
						{isScanning && scanRef.current?.isScanning && (
							<Flash scanRef={scanRef} />
						)}
					</div>

					{isScanning && scanRef.current?.isScanning && (
						<Zoom scanRef={scanRef} />
					)}

					<button
						className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/20 text-white"
						onClick={() => setActiveScanner(false)}
					>
						<FaXmark size={18} />
					</button>
				</div>
			</div>
		</Portal>
	);
}

function Zoom({ scanRef }: { scanRef: RefObject<Html5Qrcode | null> }) {
	const supportZoom =
		scanRef.current &&
		scanRef.current
			.getRunningTrackCameraCapabilities()
			.zoomFeature()
			.isSupported();
	const [zoom, setZoom] = useState(0);

	useEffect(() => {
		if (!supportZoom) return;
		const zoom = localStorage.getItem('zoom');
		if (zoom) {
			setZoom(parseFloat(zoom));

			scanRef.current
				?.getRunningTrackCameraCapabilities()
				.zoomFeature()
				.apply(parseFloat(zoom.toString()));
		}
	}, []);

	function handleZoomChange(e: ChangeEvent<HTMLInputElement>) {
		const zoom = parseFloat(e.currentTarget.value);

		if (!isNaN(zoom)) {
			setZoom(zoom);
			localStorage.setItem('zoom', zoom.toString());

			scanRef.current
				?.getRunningTrackCameraCapabilities()
				.zoomFeature()
				.apply(parseFloat(e.currentTarget.value));
		}
	}

	if (!supportZoom) return null;

	return (
		<div className="absolute top-1/2 left-1/2 flex -translate-x-1/2 translate-y-28">
			<input
				onChange={handleZoomChange}
				value={zoom}
				type="range"
				min={scanRef.current
					?.getRunningTrackCameraCapabilities()
					.zoomFeature()
					.min()}
				max={scanRef.current
					?.getRunningTrackCameraCapabilities()
					.zoomFeature()
					.max()}
				step={scanRef.current
					?.getRunningTrackCameraCapabilities()
					.zoomFeature()
					.step()}
			/>
		</div>
	);
}

function Flash({ scanRef }: { scanRef: RefObject<Html5Qrcode | null> }) {
	const supportFlash =
		scanRef.current &&
		scanRef.current
			.getRunningTrackCameraCapabilities()
			.torchFeature()
			.isSupported();
	const [active, setActiveFlash] = useState(false);

	useEffect(() => {
		if (!supportFlash) return;
		const flash = localStorage.getItem('activeFlash');
		if (flash) {
			const active = flash == 'true';
			setActiveFlash(active);
			scanRef.current
				?.getRunningTrackCameraCapabilities()
				.torchFeature()
				.apply(active);
		}
	}, []);

	function handleToggleFlash(active: boolean) {
		setActiveFlash(active);
		localStorage.setItem('activeFlash', active.toString());
		scanRef.current
			?.getRunningTrackCameraCapabilities()
			.torchFeature()
			.apply(active);
	}

	if (!supportFlash) return null;

	return (
		<Toggle
			size={'lg'}
			pressed={active}
			className="h-10 w-10 rounded-full"
			onPressedChange={handleToggleFlash}
		>
			<IoFlash />
		</Toggle>
	);
}
